import { useCallback, useRef } from 'react'
import { SELF_ENROLLMENT_STEPS } from '../../utils/steps'
import type { DocusignRefs, DocusignSetters } from '../../types'

type UseDocusignModalControlsArgs = {
  refs: Pick<DocusignRefs, 'hasLoadedDocusignIframeRef' | 'hasBlurredDuringDocusignRef'>
  setters: Pick<
    DocusignSetters,
    | 'setDocusignUrl'
    | 'setDocusignFrameKey'
    | 'setIsDocusignIframeLoading'
    | 'setIsFinalizingDocusign'
    | 'setIsDocusignModalOpen'
    | 'setIsCheckingSignature'
    | 'setIsSignatureSigned'
  >
  setSignatureCompleted: (completed: boolean) => void
  goToStep: (step: number) => void
  finalizeEnrollment: () => Promise<void>
  syncSignedStep: () => Promise<void>
}

export function useDocusignModalControls({
  refs,
  setters,
  setSignatureCompleted,
  goToStep,
  finalizeEnrollment,
  syncSignedStep
}: UseDocusignModalControlsArgs) {
  const { hasLoadedDocusignIframeRef, hasBlurredDuringDocusignRef } = refs
  const isCompletingSigningRef = useRef(false)
  const {
    setDocusignUrl,
    setDocusignFrameKey,
    setIsDocusignIframeLoading,
    setIsFinalizingDocusign,
    setIsDocusignModalOpen,
    setIsCheckingSignature,
    setIsSignatureSigned
  } = setters

  const resetDocusignFrameState = useCallback(
    (options?: { resetUrl?: boolean; bumpFrameKey?: boolean }) => {
      if (options?.resetUrl !== false) {
        setDocusignUrl(null)
      }
      if (options?.bumpFrameKey !== false) {
        setDocusignFrameKey(key => key + 1)
      }
      setIsDocusignIframeLoading(false)
      setIsFinalizingDocusign(false)
      hasLoadedDocusignIframeRef.current = false
      hasBlurredDuringDocusignRef.current = false
    },
    [
      hasBlurredDuringDocusignRef,
      hasLoadedDocusignIframeRef,
      setDocusignFrameKey,
      setDocusignUrl,
      setIsDocusignIframeLoading,
      setIsFinalizingDocusign
    ]
  )

  const closeDocusignModal = useCallback(() => {
    setIsDocusignModalOpen(false)
    setIsCheckingSignature(false)
    resetDocusignFrameState()
  }, [resetDocusignFrameState, setIsCheckingSignature, setIsDocusignModalOpen])

  const openDocusignModalWithUrl = useCallback(
    (signingUrl: string) => {
      setIsDocusignIframeLoading(true)
      setDocusignUrl(signingUrl)
      setDocusignFrameKey(key => key + 1)
      setIsDocusignModalOpen(true)
    },
    [
      setDocusignFrameKey,
      setDocusignUrl,
      setIsDocusignIframeLoading,
      setIsDocusignModalOpen
    ]
  )

  const completeSigningFlow = useCallback(async () => {
    if (isCompletingSigningRef.current) {
      return
    }

    isCompletingSigningRef.current = true
    setIsSignatureSigned(true)
    setSignatureCompleted(true)
    setIsDocusignModalOpen(false)
    setIsCheckingSignature(false)
    resetDocusignFrameState({ bumpFrameKey: false })
    goToStep(SELF_ENROLLMENT_STEPS.SIGNED_COMPLETE)

    try {
      await syncSignedStep()
    } catch {
      isCompletingSigningRef.current = false
    }
  }, [
    goToStep,
    resetDocusignFrameState,
    setIsCheckingSignature,
    setIsDocusignModalOpen,
    setIsSignatureSigned,
    setSignatureCompleted,
    syncSignedStep
  ])

  const handleContinueFromDocusign = useCallback(() => {
    closeDocusignModal()
    void finalizeEnrollment()
  }, [closeDocusignModal, finalizeEnrollment])

  const resetDocusignSessionState = useCallback(() => {
    resetDocusignFrameState()
  }, [resetDocusignFrameState])

  const setPreparingDocusignState = useCallback(() => {
    setIsDocusignIframeLoading(false)
    setIsFinalizingDocusign(false)
  }, [setIsDocusignIframeLoading, setIsFinalizingDocusign])

  const handleDocusignIframeLoad = useCallback(() => {
    if (!hasLoadedDocusignIframeRef.current) {
      hasLoadedDocusignIframeRef.current = true
      setIsDocusignIframeLoading(false)
      return
    }

    setIsDocusignIframeLoading(false)
  }, [hasLoadedDocusignIframeRef, setIsDocusignIframeLoading])

  return {
    closeDocusignModal,
    openDocusignModalWithUrl,
    completeSigningFlow,
    handleContinueFromDocusign,
    resetDocusignSessionState,
    setPreparingDocusignState,
    handleDocusignIframeLoad
  }
}
